/**
 * RentAnAgentFunnel — the "Prenajmi si AI agenta" master funnel.
 *
 * URL: /prenajmi-si-agenta
 *
 * Single hero + filter + grid of 14 agents + giant comparison table +
 * closing CTA. Copy tone matches Mika's Telegram "Profitron Bot"
 * reference: direct address, emotional urgency, 👉 CTA closers.
 *
 * Filter chips let visitors slice by use case (Sales / Marketing /
 * Operations / Compliance / Strategy) — same data file, no extra fetch.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Clock, Euro, TrendingUp, Sparkles, FlaskConical, ShieldCheck, Globe } from "lucide-react";
import { AGENTS, liveAgents, comingSoonAgents } from "../data/agentCatalog";
import LegacyFunnelHub from "../components/LegacyFunnelHub";
import AIConciergeWidget from "../components/AIConciergeWidget";
import SocialProofTicker from "../components/SocialProofTicker";
import ComingSoonWaitlist from "../components/agent-os/ComingSoonWaitlist";
import DemandCounter from "../components/agent-os/DemandCounter";
import SixtySecondHero from "../components/agent-os/SixtySecondHero";
import { useLanguage } from "../contexts/LanguageContext";
import { funnelT } from "../translations-funnel";

const CATEGORIES = {
  all: { slugs: null },
  sales: { slugs: ["cart-recovery", "sales-hunter", "pricing-intel", "newsletter"] },
  marketing: { slugs: ["product-desc", "social-media", "newsletter", "review-reply"] },
  operations: { slugs: ["helpdesk", "product-importer", "customer-onboarding"] },
  compliance: { slugs: ["gdpr-watch", "review-moderator"] },
  strategy: { slugs: ["sales-hunter", "competitive-manager", "behavior-analytics"] },
};

export default function RentAnAgentFunnel() {
  const { language } = useLanguage();
  const ft = funnelT(language);
  const [category, setCategory] = useState("all");
  const LIVE = useMemo(() => liveAgents(), []);
  const SOON = useMemo(() => comingSoonAgents(), []);
  const filtered = useMemo(() => {
    const cat = CATEGORIES[category];
    if (!cat.slugs) return LIVE;
    return LIVE.filter((a) => cat.slugs.includes(a.slug));
  }, [category, LIVE]);

  const catLabels = {
    all: ft.cat_all, sales: ft.cat_sales, marketing: ft.cat_marketing,
    operations: ft.cat_operations, compliance: ft.cat_compliance, strategy: ft.cat_strategy,
  };

  // Aggregate stats for the hero (LIVE agents only — accurate, no projections)
  const totalSavings = useMemo(() => ({
    hours_per_week: LIVE.reduce((s, a) => s + a.saves.time_hours_per_week, 0),
    eur_per_month: LIVE.reduce((s, a) => s + a.saves.money_eur_per_month, 0),
  }), [LIVE]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="rent-agent-funnel">
      {/* ─── V145: 60-Second Hero — FB-ad-ready scroll-stopper ─── */}
      <SixtySecondHero
        liveCount={LIVE.length}
        soonCount={SOON.length}
        totalSavingsEur={totalSavings.eur_per_month}
      />

      {/* ─── Hero (long-form, for organic / direct visitors) ─── */}
      <section className="px-4 sm:px-6 py-12 sm:py-20 lg:py-24 border-b border-gray-900 relative overflow-hidden">
        <div className="max-w-5xl mx-auto relative z-10">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] sm:tracking-[0.3em] font-black mb-4 text-amber-400" data-testid="funnel-eyebrow">
            <Sparkles className="w-3 h-3 inline mr-1.5" />
            {LIVE.length} {ft.funnel_eyebrow.replace("live agents", "live agents").replace(" agentov", " " + ft.funnel_stat_live.toLowerCase())} · +{SOON.length}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05] mb-5 sm:mb-6">
            {ft.funnel_h1_a} <span className="text-amber-400">{ft.funnel_h1_highlight}</span>{ft.funnel_h1_b}<br />
            {ft.funnel_h1_c}
          </h1>
          <p className="text-base sm:text-xl lg:text-2xl text-gray-300 leading-relaxed mb-7 sm:mb-8 max-w-3xl">
            {ft.funnel_subhead_a}<br />
            {ft.funnel_subhead_b}
          </p>

          {/* Stat strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6 sm:mb-8 max-w-3xl">
            <div className="border border-gray-800 p-3 sm:p-4">
              <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest font-black mb-1">{ft.funnel_stat_live}</p>
              <p className="text-xl sm:text-2xl font-black text-amber-400">{LIVE.length}<span className="text-xs sm:text-sm text-gray-500"> +{SOON.length}</span></p>
            </div>
            <div className="border border-gray-800 p-3 sm:p-4">
              <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest font-black mb-1">{ft.funnel_stat_hours}</p>
              <p className="text-xl sm:text-2xl font-black text-emerald-400">{totalSavings.hours_per_week}h</p>
            </div>
            <div className="border border-gray-800 p-3 sm:p-4">
              <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest font-black mb-1">{ft.funnel_stat_eur}</p>
              <p className="text-xl sm:text-2xl font-black text-emerald-400">€{(totalSavings.eur_per_month / 1000).toFixed(1)}k</p>
            </div>
            <div className="border border-gray-800 p-3 sm:p-4">
              <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-widest font-black mb-1">{ft.funnel_stat_trial}</p>
              <p className="text-xl sm:text-2xl font-black text-white">{ft.funnel_trial_days}</p>
            </div>
          </div>

          {/* V144 Trust strip — instant credibility under stat grid */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-7 text-[10px] sm:text-xs text-gray-400" data-testid="funnel-trust-strip">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> 1 247+ {ft.trust_eshops}</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-amber-400" /> {ft.trust_free}</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-blue-400" /> {ft.trust_gdpr}</span>
            <span className="inline-flex items-center gap-1.5 hidden sm:inline-flex"><Globe className="w-3.5 h-3.5 text-cyan-400" /> {ft.trust_data}</span>
            <span className="inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-purple-400" /> {ft.trust_languages}</span>
          </div>

          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Link to="/agent-os/onboard"
              data-testid="funnel-hero-primary-cta"
              data-cta
              className="inline-flex items-center gap-2 bg-amber-500 text-black font-black px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-widest hover:bg-amber-400">
              {ft.funnel_cta_trial} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#agents"
              className="inline-flex items-center gap-2 border border-white text-white font-black px-6 sm:px-8 py-3 sm:py-4 text-xs sm:text-sm uppercase tracking-widest hover:bg-white hover:text-black">
              {ft.funnel_cta_view}
            </a>
          </div>
        </div>
      </section>

      {/* ─── Pain quote — Telegram-style emotional copy ─── */}
      <section className="px-4 sm:px-6 py-4 border-b border-gray-900">
        <div className="max-w-5xl mx-auto">
          <SocialProofTicker />
        </div>
      </section>
      <section className="px-4 sm:px-6 py-12 sm:py-16 border-b border-gray-900 bg-neutral-950">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.3em] font-black text-red-400 mb-4">
            {ft.funnel_pain_eyebrow}
          </p>
          <p className="text-xl sm:text-2xl lg:text-3xl font-black leading-tight mb-6">
            {ft.funnel_pain_h2}
          </p>
          <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-4">
            {ft.funnel_pain_p1_a}<br/>
            {ft.funnel_pain_p1_b}
          </p>
          <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-6">
            {ft.funnel_pain_p2_a}<br/>
            {ft.funnel_pain_p2_b}
          </p>
          <p className="text-base sm:text-lg font-black text-amber-400">
            {ft.funnel_pain_cta}
          </p>
        </div>
      </section>

      {/* ─── Category filter ─── */}
      <section id="agents" className="px-4 sm:px-6 py-10 sm:py-12 border-b border-gray-900">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black mb-6">{ft.funnel_filter_h2}</h2>
          <div className="flex flex-wrap gap-2 mb-8" data-testid="funnel-category-filter">
            {Object.keys(CATEGORIES).map((key) => (
              <button
                key={key}
                onClick={() => setCategory(key)}
                data-testid={`funnel-category-${key}`}
                className={`px-3 sm:px-4 py-2 text-[10px] sm:text-[11px] font-black uppercase tracking-widest border ${
                  category === key
                    ? "border-amber-500 bg-amber-500 text-black"
                    : "border-gray-800 text-gray-400 hover:border-gray-600"
                }`}
              >
                {catLabels[key]}
              </button>
            ))}
          </div>

          {/* Agent grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="funnel-agent-grid">
            {filtered.map((a) => (
              <Link key={a.slug} to={`/agent/${a.slug}`}
                data-testid={`funnel-agent-card-${a.slug}`}
                className="border border-gray-800 p-5 hover:border-amber-500 transition-colors group block">
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-3xl">{a.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-black text-lg leading-tight group-hover:text-amber-400 transition-colors">{a.name}</h3>
                    <p className="text-[10px] uppercase tracking-widest font-black mt-1" style={{ color: a.accent }}>
                      €{a.price_eur_month}/mes
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed mb-3 line-clamp-2">{a.tagline}</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.saves.time_hours_per_week}h</span>
                  <span className="inline-flex items-center gap-1"><Euro className="w-3 h-3" /> €{(a.saves.money_eur_per_month / 1000).toFixed(1)}k</span>
                </div>
                <p className="text-[11px] text-amber-400 mt-3 group-hover:underline">
                  {ft.agent_card_detail}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Master comparison table ─── */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 border-b border-gray-900 bg-neutral-950">
        <div className="max-w-6xl mx-auto">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-black text-amber-400 mb-3">
            {ft.funnel_master_eyebrow}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black mb-8 sm:mb-10">
            {ft.funnel_master_h2}
          </h2>
          <div className="border border-gray-800 overflow-x-auto" data-testid="funnel-master-table">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="bg-neutral-900">
                  <th className="px-3 py-3 text-left text-[10px] uppercase tracking-widest font-black text-gray-400">{ft.th_agent}</th>
                  <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest font-black text-gray-400">{ft.th_price}</th>
                  <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest font-black text-gray-400 hidden sm:table-cell">{ft.th_time}</th>
                  <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest font-black text-gray-400">{ft.th_eur}</th>
                  <th className="px-3 py-3 text-right text-[10px] uppercase tracking-widest font-black text-emerald-400">{ft.th_savings}</th>
                </tr>
              </thead>
              <tbody>
                {LIVE.map((a) => (
                  <tr key={a.slug} className="border-t border-gray-900 hover:bg-neutral-900/50">
                    <td className="px-3 py-3">
                      <Link to={`/agent/${a.slug}`} className="text-white font-bold hover:text-amber-400">
                        {a.emoji} {a.name}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-gray-300">€{a.price_eur_month}</td>
                    <td className="px-3 py-3 text-right text-gray-300 hidden sm:table-cell">{a.saves.time_hours_per_week}h</td>
                    <td className="px-3 py-3 text-right text-emerald-400 font-bold">€{a.saves.money_eur_per_month.toLocaleString("sk-SK")}</td>
                    <td className="px-3 py-3 text-right font-black" style={{ color: a.accent }}>{a.vs_manual.saves_pct}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-amber-500 bg-amber-500/10">
                  <td className="px-3 py-4 font-black text-amber-300">{ft.bundle_label}</td>
                  <td className="px-3 py-4 text-right font-black text-amber-300">€198/mes</td>
                  <td className="px-3 py-4 text-right font-black text-amber-300 hidden sm:table-cell">{totalSavings.hours_per_week}h</td>
                  <td className="px-3 py-4 text-right font-black text-emerald-400">€{totalSavings.eur_per_month.toLocaleString("sk-SK")}</td>
                  <td className="px-3 py-4 text-right font-black text-amber-300">93%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </section>

      {/* ─── V143: Pripravujeme & testujeme — 12 nových agentov ─── */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 border-b border-gray-900" data-testid="coming-soon-section">
        <div className="max-w-6xl mx-auto">
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-black text-amber-400 mb-3 flex items-center gap-2">
            <FlaskConical className="w-3.5 h-3.5" />
            {ft.coming_soon_eyebrow}
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black mb-4">
            {ft.coming_soon_h2_a}{SOON.length} {ft.coming_soon_h2_b}
          </h2>
          <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-8 sm:mb-10 max-w-3xl">
            {ft.coming_soon_p_a} {ft.coming_soon_p_b} <strong className="text-amber-300">{ft.coming_soon_offer_free}</strong> {ft.coming_soon_plus} <strong className="text-amber-300">{ft.coming_soon_offer_off}</strong> {ft.coming_soon_p_c}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coming-soon-grid">
            {SOON.map((a) => (
              <div
                key={a.slug}
                data-testid={`coming-soon-card-${a.slug}`}
                className="relative border border-gray-800 bg-neutral-950 p-5 hover:border-amber-500/60 transition-colors group"
              >
                <span className="absolute top-3 right-3 text-[9px] uppercase tracking-widest font-black bg-amber-500/20 text-amber-300 px-2 py-1 border border-amber-500/30">
                  🔬 {a.eta}
                </span>
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-3xl opacity-80">{a.emoji}</div>
                  <div className="flex-1 min-w-0 pr-16">
                    <h3 className="font-black text-base leading-tight">{a.name}</h3>
                    <p className="text-[10px] uppercase tracking-widest font-black mt-1" style={{ color: a.accent }}>
                      €{a.price_eur_month}{ft.coming_soon_planned_price}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed mb-3 line-clamp-2">{a.tagline}</p>
                <div className="flex items-center gap-3 text-[11px] text-gray-500 mb-3">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.saves.time_hours_per_week}h</span>
                  <span className="inline-flex items-center gap-1"><Euro className="w-3 h-3" /> €{(a.saves.money_eur_per_month / 1000).toFixed(1)}k</span>
                </div>
                <div className="mb-3"><DemandCounter agentSlug={a.slug} /></div>
                <ComingSoonWaitlist
                  agentSlug={a.slug}
                  agentName={a.name}
                  eta={a.eta}
                  variant="inline"
                  accent={a.accent}
                />
                <Link
                  to={`/agent/${a.slug}`}
                  className="text-[11px] text-amber-400 mt-3 inline-block hover:underline"
                >
                  {ft.coming_soon_roadmap}
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-10 border border-amber-500/30 bg-amber-500/5 p-5 text-center max-w-2xl mx-auto">
            <p className="text-[10px] uppercase tracking-[0.25em] font-black text-amber-400 mb-2">
              {ft.coming_soon_priority_eyebrow}
            </p>
            <p className="text-sm text-gray-200 leading-relaxed">
              {ft.coming_soon_priority_p}
            </p>
          </div>
        </div>
      </section>

      {/* ─── Closing CTA — Telegram-style emotional ─── */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-black mb-6">
            <Zap className="w-3 h-3 inline mr-1.5" />
            {ft.funnel_close_eyebrow}
          </p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-black mb-6 leading-tight">
            {ft.funnel_close_h2}
          </p>
          <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-3">
            {ft.funnel_close_p1}
          </p>
          <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-8">
            {ft.funnel_close_p2_a}<br/>
            {ft.funnel_close_p2_b} <TrendingUp className="w-4 h-4 inline" />
          </p>
          <p className="text-base sm:text-lg font-black text-amber-400 mb-8">
            {ft.funnel_close_cta_emotional}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/agent-os/onboard"
              data-testid="funnel-final-cta"
              data-cta
              className="inline-flex items-center gap-2 bg-amber-500 text-black font-black px-8 sm:px-10 py-4 text-sm uppercase tracking-widest hover:bg-amber-400">
              {ft.funnel_close_cta_button} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-[11px] text-gray-500 mt-6">
            {ft.funnel_close_footer}
          </p>
        </div>
      </section>

      {/* ─── V144: Mobile sticky CTA — visible only ≤sm, after hero scroll ─── */}
      <div
        data-testid="funnel-mobile-sticky-cta"
        className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-amber-500/40 bg-black/95 backdrop-blur px-4 py-3"
      >
        <Link
          to="/agent-os/onboard"
          data-cta
          className="block bg-amber-500 text-black font-black text-center px-4 py-3 text-[12px] uppercase tracking-widest hover:bg-amber-400"
        >
          {ft.funnel_cta_trial} →
        </Link>
      </div>

      {/* ─── Legacy funnels (older standalone tools) ─── */}
      <LegacyFunnelHub />

      {/* ─── AI Concierge widget — behavior-triggered conversion nudge ─── */}
      <AIConciergeWidget />
    </div>
  );
}
